import type { AddedAddOn } from '@/ducks/addOns/types';
import type { TAddOn } from '@/helpers/api/graphql/queries/AddonAvailability';

import { getAddOnPrice } from '@/ducks/addOns/utils';
import { trackEvent } from '@/tagmanager/dataLayer';

import { createAddOnItemsWithRelatedId, normalizeItineraryGroup, normalizeRegions } from './utils';

type TrackViewAddonListParams = {
  addOns: TAddOn[];
  currencyCode: string;
  itinerary_group: string | null;
  regions: string[] | null;
};

type TrackAddAddonServiceParams = {
  addedAddOns: AddedAddOn[];
  currencyCode: string;
  itinerary_group: string | null;
  regions: string[] | null;
  related_item_id: string | null;
  totalPrice: number;
};

type TrackClickAddonsCTA1Params = {
  linkText: string;
};

type TrackClickAddonsCTA2Params = {
  linkText: string;
  title: string;
};

type TrackModalErrorViewParams = {
  title: string;
};

type TrackModalErrorClickParams = {
  linkText: string;
  title: string;
};

export const trackViewAddonList = ({ addOns, currencyCode, itinerary_group, regions }: TrackViewAddonListParams) => {
  const items = addOns
    .filter((addOn) => !addOn.hideOnAddonPage && addOn.addOnDetails?.price?.amount !== undefined)
    .map((addOn, index) => ({
      index: index + 1,
      item_category: 'Add-on',
      item_id: addOn.code,
      item_name: addOn.name,
      price: getAddOnPrice(addOn)?.amount || 0,
    }));

  trackEvent({
    ecommerce: {
      currency: currencyCode || 'USD',
      items,
    },
    event: 'view_addon_list',
    itinerary_group: normalizeItineraryGroup(itinerary_group),
    regions: normalizeRegions(regions),
  });
};

export const trackAddAddonService = ({
  addedAddOns,
  currencyCode,
  itinerary_group,
  regions,
  related_item_id,
  totalPrice,
}: TrackAddAddonServiceParams) => {
  const filteredAddOns = addedAddOns
    .filter((addOn) => addOn.addOnDetails?.price?.amount !== undefined)
    .map((addOn) => ({
      ...addOn,
      price: getAddOnPrice(addOn)?.amount || 0,
    }));

  const items = createAddOnItemsWithRelatedId(filteredAddOns, related_item_id);

  trackEvent({
    addons_value: totalPrice,
    ecommerce: {
      currency: currencyCode || 'USD',
      items,
    },
    event: 'add_addon_service',
    itinerary_group: normalizeItineraryGroup(itinerary_group),
    regions: normalizeRegions(regions),
  });
};

export const trackClickAddonsCTA1 = ({ linkText }: TrackClickAddonsCTA1Params) => {
  trackEvent({
    event: 'click_addons_cta1',
    link_text: linkText,
  });
};

export const trackClickAddonsCTA2 = ({ linkText, title }: TrackClickAddonsCTA2Params) => {
  trackEvent({
    event: 'click_addons_cta2',
    link_text: linkText,
    title,
  });
};

export const trackModalErrorView = ({ title }: TrackModalErrorViewParams) => {
  trackEvent({
    action: 'View',
    event: 'modal_error1',
    title,
  });
};

export const trackModalErrorClick = ({ linkText, title }: TrackModalErrorClickParams) => {
  trackEvent({
    action: 'Click',
    event: 'modal_error2',
    link_text: linkText,
    title,
  });
};
